import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';

import { Document, RadioOrSelectOption, RootState, TrackedSession } from '../../../types/common.types';
import {
  AccountabilityTemplate,
  Amount,
  AttendanceSession,
  AttendanceSessionTemplate,
  CharityActivity,
  CharityTemplate,
  Condition,
  Measurement,
  MeasurementDiagram,
  OptionalExercise,
  RecommendedExercise,
  Resource,
  SelfCareActivity,
  SurveyValueExcelFailure,
  TemplateExercise,
  Weighting
} from '../../../types/entity.types';

import { useAccountabilityTemplateApi } from '../../../api/accountability-template/useAccountabilityTemplateApi';
import { useCollectionApi } from '../../../api/collection/useCollectionApi';
import { useDocumentApi } from '../../../api/document/useDocumentApi';
import { useLeaderboardApi } from '../../../api/leaderboard/useLeaderboardApi';
import { useProgrammeTeamsApi } from '../../../api/programme-teams/useProgrammeTeamsApi';
import { useProgrammeApi } from '../../../api/programme/useProgrammeApi';
import { useV2ProgrammeApi } from '../../../api/programme/v2/useV2ProgrammeApi';
import { useResourceApi } from '../../../api/resource/useResourceApi';
import { useSurveyApi } from '../../../api/survey/useSurveyApi';
import { useTrackedSessionDashboardApi } from '../../../api/tracked-session-dashboard/useTrackedSessionDashboardApi';

import { setLeaderboard, setTeamLeaderboard } from '../../../slices/programme/accountability/accountability.slice';
import { setParentProgramme, setProgramme, setSubProgramme } from '../../../slices/programme/programme.slice';
import { addSystemNotice } from '../../../slices/system/system.slice';

import {
  initialCharityActivities,
  initialConditions,
  initialOptionalExercises,
  initialRecommendedExercises,
  initialSelfCareActivities,
  mapTrackedSessionTemplatesToTrackedSessions,
  mapTrackedSessionsToTrackedSessionTemplates
} from '../../../utils/accountability.util';
import { ERROR, MEMBERS, TEAMS, UPCOMING } from '../../../utils/constants';
import { downloadExcelFile, downloadFile, fileToBase64, generateUUID } from '../../../utils/util';

import Accountability from '../../../components/screens/programme/accountability/Accountability';

const AccountabilityContainer = () => {
  const [weightings, setWeightings] = useState<Weighting>({});
  const [measurement, setMeasurement] = useState<Measurement>({});
  const [conditions, setConditions] = useState<Condition[]>(initialConditions);
  const [recommendedExercises, setRecommendedExercises] = useState<RecommendedExercise[]>(initialRecommendedExercises);
  const [optionalExercises, setOptionalExercises] = useState<OptionalExercise[]>(initialOptionalExercises);
  const [charityActivities, setCharityActivities] = useState<CharityActivity[]>(initialCharityActivities);
  const [selfCareActivities, setSelfCareActivities] = useState<SelfCareActivity[]>(initialSelfCareActivities);
  const [attendanceSessions, setAttendanceSessions] = useState<AttendanceSession[]>([]);
  const [trackedSessions, setTrackedSessions] = useState<TrackedSession[]>([]);

  const [subProgrammeOption, setSubProgrammeOption] = useState<RadioOrSelectOption>();

  const { accountabilityTemplate, leaderboard, teamLeaderboard } = useSelector(
    ({ accountability }: RootState) => accountability
  );
  const { collections, resources } = useSelector(({ plan }: RootState) => plan);
  const { programme, parentProgramme, subProgramme } = useSelector(({ programme }: RootState) => programme);

  const dispatch = useDispatch();

  const { status, programmeExternalReference, view } = useParams();

  const [searchParams] = useSearchParams();

  const { viewProgramme, downloadTeamLeaderboardExcel: downloadTeamLeaderboardExcelRequest } = useProgrammeApi();

  const { viewParentProgramme, viewSubProgramme, viewAllProgrammes } = useV2ProgrammeApi();

  const { viewTeamLeaderboard } = useProgrammeTeamsApi();

  const { viewAllCollections } = useCollectionApi();

  const {
    createResource: createResourceRequest,
    updateResource: updateResourceRequest,
    deleteResource: deleteResourceRequest,
    viewResourcesByType
  } = useResourceApi();

  const { downloadDocument: downloadDocumentRequest, uploadDocument: uploadDocumentRequest } = useDocumentApi();

  const {
    createAccountabilityTemplate: createAccountabilityTemplateRequest,
    viewAccountabilityTemplate,
    updateAccountabilityTemplate: updateAccountabilityTemplateRequest,
    generateAccountabilityTemplateSessions
  } = useAccountabilityTemplateApi();

  const { viewLeaderboard, downloadLeaderboardExcel: downloadLeaderboardExcelRequest } = useLeaderboardApi();

  const { uploadSurvey: uploadSurveyRequest } = useSurveyApi();

  const { downloadDashboardResults } = useTrackedSessionDashboardApi();

  const retrieveMemberLeaderboard = useCallback(
    (externalReference: string) => {
      viewLeaderboard({ programmeExternalReference: externalReference });
    },
    [viewLeaderboard]
  );

  const retrieveTeamLeaderboard = useCallback(
    (externalReference: string) => {
      viewTeamLeaderboard({ programmeExternalReference: externalReference });
    },
    [viewTeamLeaderboard]
  );

  const retrieveLeaderboard = useCallback(
    (externalReference: string) => {
      if (status === UPCOMING) return;

      if (view === MEMBERS) {
        retrieveMemberLeaderboard(externalReference);
      } else if (view === TEAMS) {
        retrieveTeamLeaderboard(externalReference);
      }
    },
    [retrieveMemberLeaderboard, retrieveTeamLeaderboard, view, status]
  );

  const loadMealPlans = useCallback(
    (externalReference: string) => {
      viewResourcesByType({ programmeExternalReference: externalReference, documentType: 'meal-plan' });
    },
    [viewResourcesByType]
  );

  const loadAccountability = useCallback(
    (externalReference: string) => {
      viewAccountabilityTemplate({ programmeExternalReference: externalReference });
      viewAllCollections({ programmeExternalReference: externalReference });
      loadMealPlans(externalReference);
    },
    [viewAllCollections, loadMealPlans, viewAccountabilityTemplate]
  );

  useEffect(() => {
    if (!programmeExternalReference) return;

    const hasSubProgrammes = searchParams.get('hasSubProgrammes') === 'true';
    if (!hasSubProgrammes) {
      viewProgramme({ externalReference: programmeExternalReference });
      loadAccountability(programmeExternalReference);
      dispatch(setParentProgramme(undefined));
      dispatch(setSubProgramme(undefined));
      retrieveLeaderboard(programmeExternalReference);
      return;
    }

    viewParentProgramme({ programmeExternalReference }, (data) => {
      if (!data?.subProgrammeSummaries || data.subProgrammeSummaries.length <= 0) return;

      const subProgramme = data.subProgrammeSummaries[0];
      const subProgrammeExternalReference = subProgramme?.externalReference;

      if (!subProgrammeExternalReference) return;

      setSubProgrammeOption({ label: subProgramme.name, value: subProgrammeExternalReference });

      viewSubProgramme({ programmeExternalReference, subProgrammeExternalReference });
      loadAccountability(subProgrammeExternalReference);
      dispatch(setProgramme(undefined));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, programmeExternalReference, searchParams, viewProgramme, viewParentProgramme, viewSubProgramme]);

  useEffect(() => {
    const externalReference = subProgrammeOption?.value ?? programmeExternalReference;
    if (!externalReference) return;

    retrieveLeaderboard(externalReference);
  }, [view, subProgrammeOption, retrieveLeaderboard, programmeExternalReference]);

  useEffect(() => {
    return () => {
      dispatch(setLeaderboard(undefined));
      dispatch(setTeamLeaderboard(undefined));
    };
  }, [dispatch]);

  useEffect(() => {
    if (accountabilityTemplate) {
      const {
        measurementTemplate,
        measurement,
        conditions,
        recommendedExercise,
        optionalExercise,
        charityTemplates,
        selfCare,
        sessions,
        trackedSessionTemplates
      } = accountabilityTemplate;

      setWeightings(measurementTemplate ?? {});
      setMeasurement(measurement ?? {});
      setAttendanceSessions(sessions ?? []);
      setTrackedSessions([]);

      if (conditions && conditions.length > 0) {
        const mappedConditions: Condition[] = conditions.map((condition) => ({
          ...condition,
          externalReference: generateUUID()
        }));
        setConditions(mappedConditions);
      }

      if (recommendedExercise && recommendedExercise.length > 0) {
        const exerciseMap: Map<string, RecommendedExercise> = new Map();

        recommendedExercise.forEach((exercise): void => {
          const { name } = exercise;
          const amount: Amount = {
            externalReference: exercise.externalReference,
            fromWeek: exercise.fromWeek,
            toWeek: exercise.toWeek,
            amount: exercise.dailyAmount
          };

          const exerciseInMap = exerciseMap.get(name);
          if (exerciseInMap) {
            exerciseInMap.amounts.push(amount);
          } else {
            exerciseMap.set(name, {
              externalReference: generateUUID(),
              name,
              measurement: exercise.measurement,
              amounts: [amount]
            });
          }
        });

        setRecommendedExercises(Array.from(exerciseMap.values()));
      }

      if (optionalExercise && optionalExercise.length > 0) {
        setOptionalExercises(optionalExercise);
      }

      if (charityTemplates && charityTemplates.length > 0) {
        const charityMap: Map<string, CharityActivity> = new Map();

        charityTemplates.forEach((charityTemplate): void => {
          const { name } = charityTemplate;
          const amount: Amount = {
            externalReference: charityTemplate.externalReference,
            fromWeek: charityTemplate.fromWeek,
            toWeek: charityTemplate.toWeek,
            amount: charityTemplate.dailyAmount
          };

          const charityTemplateInMap = charityMap.get(name);
          if (charityTemplateInMap) {
            charityTemplateInMap.amounts.push(amount);
          } else {
            charityMap.set(name, {
              externalReference: generateUUID(),
              name,
              measurement: charityTemplate.measurement,
              amounts: [amount]
            });
          }
        });

        setCharityActivities(Array.from(charityMap.values()));
      }

      if (selfCare && selfCare.length > 0) {
        setSelfCareActivities(selfCare.filter((activity) => activity.name.toLowerCase() !== 'sleep'));
      }

      if (trackedSessionTemplates && trackedSessionTemplates.length > 0) {
        setTrackedSessions(mapTrackedSessionTemplatesToTrackedSessions(trackedSessionTemplates));
      }
    } else {
      setWeightings({});
      setMeasurement({});
      setConditions(initialConditions);
      setRecommendedExercises(initialRecommendedExercises);
      setOptionalExercises(initialOptionalExercises);
      setCharityActivities(initialCharityActivities);
      setSelfCareActivities(initialSelfCareActivities);
      setAttendanceSessions([]);
      setTrackedSessions([]);
    }
  }, [accountabilityTemplate]);

  const updateWeighting = (field: keyof Weighting, value: number) => {
    const copyOfWeightings = structuredClone(weightings);
    copyOfWeightings[field] = value;
    setWeightings(copyOfWeightings);
  };

  const uploadSurvey = async (file: File, onError: (errors: SurveyValueExcelFailure[]) => void) => {
    const base64 = await fileToBase64(file);
    dispatch(
      uploadSurveyRequest(
        {
          programmeExternalReference: subProgrammeOption?.value ?? programmeExternalReference!,
          data: base64
        },
        onError
      )
    );
  };

  const updateMeasurement = (field: keyof Measurement, value: MeasurementDiagram | string | undefined) => {
    const copyOfMeasurement: Measurement = structuredClone(measurement);

    if (field === 'measurementDiagram') {
      copyOfMeasurement[field] = value as MeasurementDiagram;
    } else {
      copyOfMeasurement[field] = value as string;
    }

    setMeasurement(copyOfMeasurement);
  };

  const uploadDocument = async (document: Document): Promise<string> => {
    return new Promise((resolve) => {
      uploadDocumentRequest(document, (documentExternalReference) => {
        resolve(documentExternalReference);
      });
    });
  };

  const downloadDocument = (resource: Resource) => {
    const { documentExternalReference, name } = resource;

    if (!documentExternalReference || !name) return;

    downloadDocumentRequest({ documentExternalReference }, (data) => downloadFile(data, name));
  };

  const createMealPlan = async (collectionExternalReference: string, resource: Resource) => {
    const documentExternalReference = await uploadDocument(resource.document as Document);
    delete resource.document;

    const externalReference = subProgrammeOption?.value ?? programmeExternalReference;

    createResourceRequest(
      {
        programmeExternalReference: externalReference!,
        collectionExternalReference,
        resource: {
          ...resource,
          documentExternalReference
        }
      },
      () => loadMealPlans(externalReference!)
    );
  };

  const updateMealPlan = async (resource: Resource) => {
    if (resource.document) {
      const documentExternalReference = await uploadDocument(resource.document);
      resource.documentExternalReference = documentExternalReference;
      delete resource.document;
    }

    const externalReference = subProgrammeOption?.value ?? programmeExternalReference;

    updateResourceRequest(
      {
        programmeExternalReference: externalReference!,
        collectionExternalReference: resource.collectionExternalReference!,
        resource
      },
      () => loadMealPlans(externalReference!)
    );
  };

  const deleteMealPlan = (collectionExternalReference: string, resourceExternalReference: string) => {
    const externalReference = subProgrammeOption?.value ?? programmeExternalReference;

    deleteResourceRequest(
      {
        programmeExternalReference: externalReference!,
        planExternalReference: collectionExternalReference,
        resourceExternalReference
      },
      () => loadMealPlans(externalReference!)
    );
  };

  const createCondition = () => {
    const condition: Condition = {
      externalReference: generateUUID(),
      optionOne: '',
      optionTwo: ''
    };

    setConditions((prevState) => [...prevState, condition]);
  };

  const updateCondition = (externalReference: string, field: keyof Condition, value: string) => {
    const copyOfConditions = structuredClone(conditions);
    copyOfConditions.forEach((condition) => {
      if (condition.externalReference === externalReference) {
        condition[field] = value;
      }
    });

    setConditions(copyOfConditions);
  };

  const deleteCondition = (externalReference: string) => {
    setConditions(conditions.filter((condition) => condition.externalReference !== externalReference));
  };

  const createRecommendedExercise = () => {
    const exercise: RecommendedExercise = {
      externalReference: generateUUID(),
      name: '',
      measurement: '',
      amounts: [{ externalReference: generateUUID(), fromWeek: '', toWeek: '', amount: '' }]
    };

    setRecommendedExercises((prevState) => [...prevState, exercise]);
  };

  const updateRecommendedExercise = (
    externalReference: string,
    field: keyof Omit<RecommendedExercise, 'amounts'>,
    value: string
  ) => {
    const copyOfRecommendedExercises = structuredClone(recommendedExercises);
    copyOfRecommendedExercises.forEach((exercise) => {
      if (exercise.externalReference === externalReference) {
        exercise[field] = value;
      }
    });

    setRecommendedExercises(copyOfRecommendedExercises);
  };

  const deleteRecommendedExercise = (externalReference: string) => {
    setRecommendedExercises(
      recommendedExercises.filter((exercise) => exercise.externalReference !== externalReference)
    );
  };

  const createExerciseAmount = (externalReference: string) => {
    const copyOfRecommendedExercises = structuredClone(recommendedExercises);
    copyOfRecommendedExercises.forEach((exercise) => {
      if (exercise.externalReference === externalReference) {
        const amount: Amount = { externalReference: generateUUID(), fromWeek: '', toWeek: '', amount: '' };
        exercise.amounts.push(amount);
      }
    });

    setRecommendedExercises(copyOfRecommendedExercises);
  };

  const updateExerciseAmount = (
    exerciseExternalReference: string,
    amountExternalReference: string,
    field: keyof Amount,
    value: string
  ) => {
    const copyOfRecommendedExercises = structuredClone(recommendedExercises);
    copyOfRecommendedExercises.forEach((exercise) => {
      if (exercise.externalReference === exerciseExternalReference) {
        exercise.amounts.forEach((amount) => {
          if (amount.externalReference === amountExternalReference) {
            amount[field] = value;
          }
        });
      }
    });
    setRecommendedExercises(copyOfRecommendedExercises);
  };

  const deleteExerciseAmount = (exerciseExternalReference: string, amountExternalReference: string) => {
    const copyOfRecommendedExercises = structuredClone(recommendedExercises);
    copyOfRecommendedExercises.forEach((exercise) => {
      if (exercise.externalReference === exerciseExternalReference) {
        exercise.amounts = exercise.amounts.filter((amount) => amount.externalReference !== amountExternalReference);
      }
    });
    setRecommendedExercises(copyOfRecommendedExercises);
  };

  const createOptionalExercise = () => {
    const exercise: OptionalExercise = {
      externalReference: generateUUID(),
      name: '',
      measurement: ''
    };

    setOptionalExercises((prevState) => [...prevState, exercise]);
  };

  const updateOptionalExercise = (externalReference: string, field: keyof OptionalExercise, value: string) => {
    const copyOfOptionalExercises = structuredClone(optionalExercises);
    copyOfOptionalExercises.forEach((exercise) => {
      if (exercise.externalReference === externalReference) {
        exercise[field] = value;
      }
    });

    setOptionalExercises(copyOfOptionalExercises);
  };

  const deleteOptionalExercise = (externalReference: string) => {
    setOptionalExercises(optionalExercises.filter((exercise) => exercise.externalReference !== externalReference));
  };

  const createCharityActivity = () => {
    const charityActivity: CharityActivity = {
      externalReference: generateUUID(),
      name: '',
      measurement: '',
      amounts: [{ externalReference: generateUUID(), fromWeek: '', toWeek: '', amount: '' }]
    };

    setCharityActivities((prevState) => [...prevState, charityActivity]);
  };

  const updateCharityActivity = (
    externalReference: string,
    field: keyof Omit<CharityActivity, 'amounts'>,
    value: string
  ) => {
    const copyOfCharityActivities = structuredClone(charityActivities);
    copyOfCharityActivities.forEach((charityActivity) => {
      if (charityActivity.externalReference === externalReference) {
        charityActivity[field] = value;
      }
    });

    setCharityActivities(copyOfCharityActivities);
  };

  const deleteCharityActivity = (externalReference: string) => {
    setCharityActivities(
      charityActivities.filter((charityActivity) => charityActivity.externalReference !== externalReference)
    );
  };

  const createCharityAmount = (externalReference: string) => {
    const copyOfCharityExercises = structuredClone(charityActivities);
    copyOfCharityExercises.forEach((charityActivity) => {
      if (charityActivity.externalReference === externalReference) {
        const amount: Amount = { externalReference: generateUUID(), fromWeek: '', toWeek: '', amount: '' };
        charityActivity.amounts.push(amount);
      }
    });

    setCharityActivities(copyOfCharityExercises);
  };

  const updateCharityAmount = (
    charityActivityExternalReference: string,
    amountExternalReference: string,
    field: keyof Amount,
    value: string
  ) => {
    const copyOfCharityActivities = structuredClone(charityActivities);
    copyOfCharityActivities.forEach((charityActivity) => {
      if (charityActivity.externalReference === charityActivityExternalReference) {
        charityActivity.amounts.forEach((amount) => {
          if (amount.externalReference === amountExternalReference) {
            amount[field] = value;
          }
        });
      }
    });

    setCharityActivities(copyOfCharityActivities);
  };

  const deleteCharityAmount = (charityActivityExternalReference: string, amountExternalReference: string) => {
    const copyOfCharityActivities = structuredClone(charityActivities);
    copyOfCharityActivities.forEach((charityActivity) => {
      if (charityActivity.externalReference === charityActivityExternalReference) {
        charityActivity.amounts = charityActivity.amounts.filter(
          (amount) => amount.externalReference !== amountExternalReference
        );
      }
    });

    setCharityActivities(copyOfCharityActivities);
  };

  const createSelfCareActivity = () => {
    const activity: SelfCareActivity = {
      externalReference: generateUUID(),
      name: ''
    };

    setSelfCareActivities((prevState) => [...prevState, activity]);
  };

  const updateSelfCareActivity = (externalReference: string, value: string) => {
    const copyOfSelfCareActivities = structuredClone(selfCareActivities);
    copyOfSelfCareActivities.forEach((activity) => {
      if (activity.externalReference === externalReference) {
        activity.name = value;
      }
    });

    setSelfCareActivities(copyOfSelfCareActivities);
  };

  const deleteSelfCareActivity = (externalReference: string) => {
    setSelfCareActivities(selfCareActivities.filter((activity) => activity.externalReference !== externalReference));
  };

  const updateAttendanceSession = (updatedSession: Partial<AttendanceSession>) => {
    const copyOfSessions = structuredClone(attendanceSessions);
    copyOfSessions.forEach((session) => {
      if (session.externalReference === updatedSession.externalReference) {
        session.name = updatedSession.name as string;
        session.startTime = updatedSession.startTime as Date;
        session.endTime = updatedSession.endTime as Date;
        session.date = updatedSession.date;
      }
    });

    setAttendanceSessions(copyOfSessions);
  };

  const deleteAttendanceSession = (externalReference: string) => {
    setAttendanceSessions(attendanceSessions.filter((session) => session.externalReference !== externalReference));
  };

  const createTrackedSession = (session: TrackedSession) => {
    setTrackedSessions((prevState) => [...prevState, session]);
  };

  const updateTrackedSession = (updatedSession: TrackedSession) => {
    const copyOfSessions = structuredClone(trackedSessions);

    const sessionIndex = copyOfSessions.findIndex((session) => session.uuid === updatedSession.uuid);
    if (sessionIndex !== -1) {
      copyOfSessions[sessionIndex] = { ...copyOfSessions[sessionIndex], ...updatedSession };
    }

    setTrackedSessions(copyOfSessions);
  };

  const deleteTrackedSession = (uuid: string) => {
    setTrackedSessions(trackedSessions.filter((session) => session.uuid !== uuid));
  };

  const validateMeasurements = () => {
    if (!measurement.measurementFrequency || !measurement.measurementStartDay) {
      dispatch(addSystemNotice({ type: ERROR, message: 'Measurements section incomplete' }));
      return false;
    }

    return true;
  };

  const validateWeightings = () => {
    let total = 0;

    total += Number(weightings.weight) || 0;
    total += Number(weightings.cm) || 0;
    total += Number(weightings.steps) || 0;
    total += Number(weightings.objectives) || 0;

    if (subProgramme?.charityWorkEnabled ?? programme?.charityWork) {
      total += Number(weightings.charity) || 0;
    }

    if (total !== 100) {
      dispatch(addSystemNotice({ type: ERROR, message: 'Weightings do not add up to 100%' }));
      return false;
    }

    return true;
  };

  const saveAccountabilityTemplate = (onSuccess: () => void) => {
    const measurementsValid = validateMeasurements();
    const weightingsValid = validateWeightings();

    if (!measurementsValid || !weightingsValid) return;

    const mappedRecommendedExercises: TemplateExercise[] = [];
    recommendedExercises.forEach((exercise) => {
      exercise.amounts.forEach((amount) => {
        mappedRecommendedExercises.push({
          externalReference: amount.externalReference,
          name: exercise.name,
          dailyAmount: amount.amount,
          measurement: exercise.measurement,
          fromWeek: amount.fromWeek,
          toWeek: amount.toWeek
        });
      });
    });

    const mappedSessions: AttendanceSession[] = [];
    attendanceSessions.forEach((session) => {
      mappedSessions.push({
        ...session,
        startDate: new Date(session.startDate)
      });
    });

    const mappedCharityActivities: CharityTemplate[] = [];
    charityActivities.forEach((charityActivity) => {
      charityActivity.amounts.forEach((amount) => {
        mappedCharityActivities.push({
          externalReference: amount.externalReference,
          name: charityActivity.name,
          dailyAmount: amount.amount,
          measurement: charityActivity.measurement,
          fromWeek: amount.fromWeek,
          toWeek: amount.toWeek
        });
      });
    });

    const templateToSave: AccountabilityTemplate = {
      measurementTemplate: weightings,
      conditions,
      measurement,
      recommendedExercise: mappedRecommendedExercises,
      optionalExercise: optionalExercises,
      selfCare: selfCareActivities,
      sessions: mappedSessions,
      programmeObjectReference: subProgrammeOption?.value ?? programmeExternalReference,
      charityTemplates: mappedCharityActivities,
      trackedSessionTemplates: mapTrackedSessionsToTrackedSessionTemplates(trackedSessions)
    };

    if (accountabilityTemplate) {
      updateAccountabilityTemplate(templateToSave, onSuccess);
    } else {
      createAccountabilityTemplate(templateToSave, onSuccess);
    }
  };

  const createAccountabilityTemplate = (accountabilityTemplate: AccountabilityTemplate, onSuccess: () => void) => {
    createAccountabilityTemplateRequest({ accountabilityTemplate }, () => {
      if (status === UPCOMING) {
        viewAllProgrammes();
      }

      onSuccess();
    });
  };

  const updateAccountabilityTemplate = (accountabilityTemplate: AccountabilityTemplate, onSuccess: () => void) => {
    updateAccountabilityTemplateRequest({ accountabilityTemplate }, () => {
      if (status === UPCOMING) {
        viewAllProgrammes();
      }

      onSuccess();
    });
  };

  const generateAttendanceSessions = (sessionTemplate: AttendanceSessionTemplate, onSuccess: () => void) => {
    generateAccountabilityTemplateSessions(sessionTemplate, (sessionTemplates) => {
      sessionTemplates = sessionTemplates?.map((sessionTemplate) => ({
        ...sessionTemplate,
        externalReference: generateUUID()
      }));
      if (sessionTemplates) {
        setAttendanceSessions((prevState) => [...prevState, ...sessionTemplates]);
      }
      onSuccess();
    });
  };

  const downloadLeaderboardExcel = () => {
    const programmeName = subProgrammeOption?.label ?? programme?.name;
    const externalReference = subProgrammeOption?.value ?? programmeExternalReference!;

    downloadLeaderboardExcelRequest(
      {
        programmeExternalReference: externalReference,
        programmeName: programmeName as string
      },
      (base64String) => downloadExcelFile(base64String, `${programmeName} - Leaderboard.xlsx`)
    );
  };

  const downloadTeamLeaderboardExcel = () => {
    const programmeName = subProgrammeOption?.label ?? programme?.name;
    const externalReference = subProgrammeOption?.value ?? programmeExternalReference!;

    downloadTeamLeaderboardExcelRequest({
      programmeExternalReference: externalReference,
      programmeName: programmeName as string
    });
  };

  const downloadTrackedSessionDashboardExcel = (sessionEventExternalReference: string) => {
    downloadDashboardResults(
      {
        sessionEventExternalReference
      },
      (data) => downloadExcelFile(data.data, data.documentName)
    );
  };

  const handleSubProgrammeChange = (option: RadioOrSelectOption) => {
    if (option.value === subProgrammeOption?.value) return;

    setSubProgrammeOption(option);

    viewSubProgramme({
      programmeExternalReference: programmeExternalReference!,
      subProgrammeExternalReference: option.value
    });
    loadAccountability(option.value);

    if (status === UPCOMING) {
      setWeightings({});
      setMeasurement({});
      setConditions(initialConditions);
      setRecommendedExercises(initialRecommendedExercises);
      setOptionalExercises(initialOptionalExercises);
      setCharityActivities(initialCharityActivities);
      setSelfCareActivities(initialSelfCareActivities);
      setAttendanceSessions([]);
      setTrackedSessions([]);
    }
  };

  return (
    <Accountability
      status={status as string}
      programme={programme}
      parentProgramme={parentProgramme}
      subProgramme={subProgramme}
      subProgrammeOption={subProgrammeOption}
      leaderboard={leaderboard}
      teamLeaderboard={teamLeaderboard}
      accountabilityTemplate={accountabilityTemplate}
      collections={collections}
      weightings={weightings}
      measurement={measurement}
      mealPlans={resources}
      conditions={conditions}
      recommendedExercises={recommendedExercises}
      optionalExercises={optionalExercises}
      charityActivities={charityActivities}
      selfCareActivities={selfCareActivities}
      attendanceSessions={attendanceSessions}
      trackedSessions={trackedSessions}
      updateWeighting={updateWeighting}
      uploadSurvey={uploadSurvey}
      updateMeasurement={updateMeasurement}
      createMealPlan={createMealPlan}
      updateMealPlan={updateMealPlan}
      deleteMealPlan={deleteMealPlan}
      createCondition={createCondition}
      updateCondition={updateCondition}
      deleteCondition={deleteCondition}
      createRecommendedExercise={createRecommendedExercise}
      updateRecommendedExercise={updateRecommendedExercise}
      deleteRecommendedExercise={deleteRecommendedExercise}
      createExerciseAmount={createExerciseAmount}
      updateExerciseAmount={updateExerciseAmount}
      deleteExerciseAmount={deleteExerciseAmount}
      createOptionalExercise={createOptionalExercise}
      updateOptionalExercise={updateOptionalExercise}
      deleteOptionalExercise={deleteOptionalExercise}
      createCharityActivity={createCharityActivity}
      updateCharityActivity={updateCharityActivity}
      deleteCharityActivity={deleteCharityActivity}
      createCharityAmount={createCharityAmount}
      updateCharityAmount={updateCharityAmount}
      deleteCharityAmount={deleteCharityAmount}
      createSelfCareActivity={createSelfCareActivity}
      updateSelfCareActivity={updateSelfCareActivity}
      deleteSelfCareActivity={deleteSelfCareActivity}
      updateAttendanceSession={updateAttendanceSession}
      deleteAttendanceSession={deleteAttendanceSession}
      createTrackedSession={createTrackedSession}
      updateTrackedSession={updateTrackedSession}
      deleteTrackedSession={deleteTrackedSession}
      saveAccountabilityTemplate={saveAccountabilityTemplate}
      generateAttendanceSessions={generateAttendanceSessions}
      downloadLeaderboardExcel={downloadLeaderboardExcel}
      downloadDocument={downloadDocument}
      downloadTeamLeaderboardExcel={downloadTeamLeaderboardExcel}
      onSubProgrammeChange={handleSubProgrammeChange}
      loadAccountability={loadAccountability}
      downloadTrackedSessionDashboardExcel={downloadTrackedSessionDashboardExcel}
    />
  );
};

export default AccountabilityContainer;
